import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  AlertTriangle, Plus, Clock, CheckCircle, User,
  MapPin, MessageSquare, ChevronDown, ChevronUp, Filter
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const typeConfig = {
  caida: { label: "Caída", bg: "bg-orange-50", text: "text-orange-700" },
  agresion: { label: "Agresión", bg: "bg-red-50", text: "text-red-700" },
  error_medicacion: { label: "Error Medicación", bg: "bg-fuchsia-50", text: "text-fuchsia-700" },
  fuga: { label: "Fuga", bg: "bg-violet-50", text: "text-violet-700" },
  autolesion: { label: "Autolesión", bg: "bg-rose-50", text: "text-rose-700" },
  otro: { label: "Otro", bg: "bg-slate-50", text: "text-slate-600" },
};

const severityConfig = {
  leve: { label: "Leve", bg: "bg-emerald-50", text: "text-emerald-700" },
  moderado: { label: "Moderado", bg: "bg-amber-50", text: "text-amber-700" },
  grave: { label: "Grave", bg: "bg-rose-50", text: "text-rose-600" },
};

const statusConfig = {
  abierto: { label: "Abierto", bg: "bg-rose-50", text: "text-rose-600", icon: AlertTriangle },
  en_seguimiento: { label: "En seguimiento", bg: "bg-amber-50", text: "text-amber-700", icon: Clock },
  cerrado: { label: "Cerrado", bg: "bg-emerald-50", text: "text-emerald-700", icon: CheckCircle },
};

export default function IncidentsPage() {
  const { user } = useAuth();
  const canReport = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("reportar_incidentes");

  const [incidents, setIncidents] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [followupDialog, setFollowupDialog] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");

  const [form, setForm] = useState({
    resident_id: "",
    incident_type: "caida",
    severity: "leve",
    description: "",
    actions_taken: "",
    witnesses: "",
    location: "",
  });

  const [followupForm, setFollowupForm] = useState({ content: "", status: "" });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (severityFilter !== "all") params.severity = severityFilter;
      const [incRes, resRes] = await Promise.all([
        api.get("/incidents", { params }),
        api.get("/residents")
      ]);
      setIncidents(incRes.data);
      setResidents(resRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [statusFilter, severityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.witnesses) delete payload.witnesses;
      if (!payload.location) delete payload.location;
      await api.post("/incidents", payload);
      toast.success("Incidente reportado correctamente");
      setDialogOpen(false);
      setForm({ resident_id: "", incident_type: "caida", severity: "leve", description: "", actions_taken: "", witnesses: "", location: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al reportar incidente");
    }
  };

  const handleFollowup = async (e) => {
    e.preventDefault();
    if (!followupDialog) return;
    try {
      const payload = { content: followupForm.content };
      if (followupForm.status) payload.status = followupForm.status;
      await api.post(`/incidents/${followupDialog}/followup`, payload);
      toast.success("Seguimiento agregado");
      setFollowupDialog(null);
      setFollowupForm({ content: "", status: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const openCount = incidents.filter(i => i.status === "abierto").length;
  const followingCount = incidents.filter(i => i.status === "en_seguimiento").length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="incidents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">
            Incidentes
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            {openCount > 0 && <span className="text-rose-500 font-medium">{openCount} abierto(s)</span>}
            {openCount > 0 && followingCount > 0 && " · "}
            {followingCount > 0 && <span className="text-amber-600 font-medium">{followingCount} en seguimiento</span>}
            {openCount === 0 && followingCount === 0 && "Sin incidentes pendientes"}
          </p>
        </div>
        {canReport && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="rounded-xl px-5 h-10 bg-rose-600 text-white hover:bg-rose-700 active:scale-[0.98] transition-all text-sm font-semibold"
                data-testid="report-incident-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Reportar Incidente
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">
                  Reportar Incidente
                </DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">
                  Completar todos los campos del reporte
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Residente afectado</Label>
                  <Select value={form.resident_id} onValueChange={v => setForm({ ...form, resident_id: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="incident-resident-select">
                      <SelectValue placeholder="Seleccionar residente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {residents.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name} — Hab. {r.room_number || "S/N"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                    <Select value={form.incident_type} onValueChange={v => setForm({ ...form, incident_type: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="incident-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="caida">Caída</SelectItem>
                        <SelectItem value="agresion">Agresión</SelectItem>
                        <SelectItem value="error_medicacion">Error Medicación</SelectItem>
                        <SelectItem value="fuga">Fuga</SelectItem>
                        <SelectItem value="autolesion">Autolesión</SelectItem>
                        <SelectItem value="otro">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Severidad</Label>
                    <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="incident-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leve">Leve</SelectItem>
                        <SelectItem value="moderado">Moderado</SelectItem>
                        <SelectItem value="grave">Grave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Lugar del incidente</Label>
                  <Input
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    placeholder="Ej: Habitación 101, Comedor, Pasillo..."
                    className="rounded-xl h-10 bg-slate-50 border-slate-200"
                    data-testid="incident-location-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Descripción del incidente</Label>
                  <Textarea
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Describir qué sucedió, cuándo, cómo..."
                    className="rounded-xl bg-slate-50 min-h-[80px] border-slate-200"
                    required
                    data-testid="incident-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Acciones tomadas</Label>
                  <Textarea
                    value={form.actions_taken}
                    onChange={e => setForm({ ...form, actions_taken: e.target.value })}
                    placeholder="Qué medidas se tomaron inmediatamente..."
                    className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200"
                    required
                    data-testid="incident-actions-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Testigos (opcional)</Label>
                  <Input
                    value={form.witnesses}
                    onChange={e => setForm({ ...form, witnesses: e.target.value })}
                    placeholder="Nombres de testigos..."
                    className="rounded-xl h-10 bg-slate-50 border-slate-200"
                    data-testid="incident-witnesses-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={!form.resident_id || !form.description.trim() || !form.actions_taken.trim()}
                  className="w-full rounded-xl h-10 bg-rose-600 text-white font-semibold hover:bg-rose-700 active:scale-[0.98] transition-all"
                  data-testid="submit-incident-btn"
                >
                  Reportar Incidente
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setLoading(true); }}>
            <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200 w-[180px]" data-testid="incidents-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="abierto">Abiertos</SelectItem>
              <SelectItem value="en_seguimiento">En seguimiento</SelectItem>
              <SelectItem value="cerrado">Cerrados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={v => { setSeverityFilter(v); setLoading(true); }}>
            <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200 w-[180px]" data-testid="incidents-severity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las severidades</SelectItem>
              <SelectItem value="leve">Leve</SelectItem>
              <SelectItem value="moderado">Moderado</SelectItem>
              <SelectItem value="grave">Grave</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400">{incidents.length} incidente(s)</span>
        </CardContent>
      </Card>

      {/* Incidents list */}
      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando incidentes...</p>
      ) : incidents.length === 0 ? (
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No hay incidentes registrados con estos filtros</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {incidents.map(inc => {
            const tCfg = typeConfig[inc.incident_type] || typeConfig.otro;
            const sCfg = severityConfig[inc.severity] || severityConfig.leve;
            const stCfg = statusConfig[inc.status] || statusConfig.abierto;
            const isExpanded = expandedId === inc.id;
            const StatusIcon = stCfg.icon;

            return (
              <Card
                key={inc.id}
                className={`rounded-2xl border-slate-100 shadow-sm transition-all ${
                  inc.severity === "grave" ? "border-l-4 border-l-rose-400" :
                  inc.severity === "moderado" ? "border-l-4 border-l-amber-400" : ""
                }`}
                data-testid={`incident-${inc.id}`}
              >
                <CardContent className="p-4">
                  <div
                    className="flex items-start gap-3 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : inc.id)}
                    data-testid={`incident-toggle-${inc.id}`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${tCfg.bg} flex items-center justify-center flex-shrink-0`}>
                      <AlertTriangle className={`w-5 h-5 ${tCfg.text}`} strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${tCfg.bg} ${tCfg.text}`}>
                          {tCfg.label}
                        </Badge>
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${sCfg.bg} ${sCfg.text}`}>
                          {sCfg.label}
                        </Badge>
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${stCfg.bg} ${stCfg.text}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {stCfg.label}
                        </Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-900 mt-1 line-clamp-2">{inc.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <User className="w-3 h-3" /> {inc.resident_name || "—"}
                        </span>
                        {inc.location && (
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {inc.location}
                          </span>
                        )}
                        <span className="text-xs text-slate-400">
                          {new Date(inc.created_at).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-300" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-300" />
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-fade-in-up">
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1">Acciones Tomadas</p>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{inc.actions_taken}</p>
                      </div>
                      {inc.witnesses && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1">Testigos</p>
                          <p className="text-sm text-slate-700">{inc.witnesses}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-1">Reportado por</p>
                        <p className="text-sm text-slate-700">{inc.reported_by_name} — {new Date(inc.created_at).toLocaleString("es-AR")}</p>
                      </div>

                      {/* Followups */}
                      {inc.followups?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold mb-2">Seguimiento</p>
                          <div className="space-y-2 pl-4 border-l-2 border-slate-100">
                            {inc.followups.map(fu => (
                              <div key={fu.id} className="bg-slate-50 rounded-xl p-3">
                                <p className="text-sm text-slate-700 whitespace-pre-wrap">{fu.content}</p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {fu.created_by_name} — {new Date(fu.created_at).toLocaleString("es-AR")}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add followup */}
                      {canReport && inc.status !== "cerrado" && (
                        <Dialog open={followupDialog === inc.id} onOpenChange={v => setFollowupDialog(v ? inc.id : null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              className="rounded-xl h-9 text-sm border-slate-200 text-slate-700"
                              data-testid={`followup-btn-${inc.id}`}
                            >
                              <MessageSquare className="w-4 h-4 mr-2" /> Agregar Seguimiento
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-2xl sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Seguimiento</DialogTitle>
                              <DialogDescription className="text-slate-400 text-sm">Agregar nota de seguimiento al incidente</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleFollowup} className="space-y-4 mt-2">
                              <div className="space-y-2">
                                <Label className="font-medium text-sm text-slate-700">Nota</Label>
                                <Textarea
                                  value={followupForm.content}
                                  onChange={e => setFollowupForm({ ...followupForm, content: e.target.value })}
                                  placeholder="Describir el seguimiento..."
                                  className="rounded-xl bg-slate-50 min-h-[80px] border-slate-200"
                                  required
                                  data-testid="followup-content-input"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="font-medium text-sm text-slate-700">Cambiar estado (opcional)</Label>
                                <Select value={followupForm.status} onValueChange={v => setFollowupForm({ ...followupForm, status: v })}>
                                  <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="followup-status-select">
                                    <SelectValue placeholder="Mantener actual" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="en_seguimiento">En seguimiento</SelectItem>
                                    <SelectItem value="cerrado">Cerrado</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                type="submit"
                                disabled={!followupForm.content.trim()}
                                className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all"
                                data-testid="submit-followup-btn"
                              >
                                Agregar Seguimiento
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
